import { useParams } from 'react-router-dom';

import { Divider, Grid, InputAdornment } from '@mui/material';
import useFeatureFlags from 'api/useFeatureFlags';
import { Image, OfferUiProps } from 'common/contracts';
import { EAssetType, EFeatureFlag } from 'constants/enums';
import { useUploads } from 'hooks/useUpload';
import { defaultBackgroundImage } from 'utils/selectOptionsUtils';

import AcCard from 'components/AcCard/AcCard';
import AcGradientInput from 'components/AcGradientInput/AcGradientInput';
import AcInput from 'components/AcInput/AcInput';
import AcSelect from 'components/AcSelect/AcSelect';
import { EAcSelectItemRenderType } from 'components/AcSelect/AcSelect.types';
import AcSolidInput from 'components/AcSolidInput/AcSolidInput';

const RollingOfferUiForm: React.FC<OfferUiProps> = ({
  values,
  errors,
  touched,
  handleChange,
  handleBlur,
  setFieldValue,
  addPictureInputRef,
  getImages,
  setCurrentUploadingField,
  currentUploadingField,
  dup
}) => {
  const { publisherId } = useParams();

  const { uploadImage, getAssetUploadMessage } = useUploads();
  const { featureFlags } = useFeatureFlags({ publisherId });

  const hasFeatureFlagArrowImage =
    featureFlags?.[
      EFeatureFlag.DASHBOARD_ROLLING_ARROW_IMAGE
    ];

  return (
    <>
      <AcCard
        stackContainer={false}
        title="Main Offer Design"
        padded={false}
        className="offersUIFormCard"
      >
        <Grid container columnSpacing={{ xs: 1 }}>
          <Grid item xs={6}>
            <input
              style={{ display: 'none' }}
              type="file"
              onChange={(e) =>
                uploadImage(
                  e,
                  addPictureInputRef.current as HTMLInputElement,
                  'backgroundImage',
                  setFieldValue,
                  setCurrentUploadingField,
                  currentUploadingField
                )
              }
              ref={addPictureInputRef}
              accept="image/*"
            />
            <AcSelect
              header="Offer Header"
              name="rollingOffer.headerImage"
              value={values.rollingOffer?.headerImage || ''}
              uploadConfig={{
                onUploadSuccess: async (url: string) => {
                  await getImages.refetch();
                  setFieldValue('rollingOffer.headerImage', url);
                },
                uploadType: EAssetType.OFFER_HEADER,
                uploadMessage: getAssetUploadMessage(values.offerUiType)
              }}
              renderType={EAcSelectItemRenderType.IMAGE}
              items={getImages.data
                .filter((p: Image) => p.type === EAssetType.OFFER_HEADER)
                .map((picture: Image) => {
                  return {
                    content: picture.name,
                    key: picture.url,
                    value: picture.url,
                    url: picture.url,
                    name: picture.name
                  };
                })}
              onChange={handleChange}
              onBlur={handleBlur}
              onClear={() => {
                setFieldValue('rollingOffer.headerImage', '');
              }}
              imgHeight={'32'}
            />
          </Grid>
          <Grid item xs={6}>
            <input
              style={{ display: 'none' }}
              type="file"
              onChange={(e) =>
                uploadImage(
                  e,
                  addPictureInputRef.current as HTMLInputElement,
                  'backgroundImage',
                  setFieldValue,
                  setCurrentUploadingField,
                  currentUploadingField
                )
              }
              ref={addPictureInputRef}
              accept="image/*"
            />
            <AcSelect
              header="Offer background image"
              name="backgroundImage"
              value={values.backgroundImage}
              uploadConfig={{
                onUploadSuccess: async (url: string) => {
                  await getImages.refetch();
                  setFieldValue('backgroundImage', url);
                },
                uploadType: EAssetType.BG_BUNDLE,
                uploadMessage: getAssetUploadMessage(values.offerUiType)
              }}
              renderType={EAcSelectItemRenderType.IMAGE}
              items={[
                defaultBackgroundImage,
                ...getImages.data
                  .filter((p: any) => p.type === EAssetType.BG_BUNDLE)
                  .map((picture: any) => {
                    return {
                      content: picture.name,
                      key: picture.url,
                      value: picture.url,
                      url: picture.url,
                      name: picture.name
                    };
                  })
              ]}
              onChange={handleChange}
              onBlur={handleBlur}
              onClear={() => {
                setFieldValue('backgroundImage', '');
              }}
              error={touched.backgroundImage && Boolean(errors.backgroundImage)}
              helperText={
                touched.backgroundImage || dup
                  ? errors.backgroundImage?.toString()
                  : ''
              }
              imgHeight={'32'}
            />
          </Grid>
        </Grid>
        <Grid container columnSpacing={{ xs: 1 }}>
          <Grid item xs={6}>
            <AcGradientInput
              required
              header="Offer background color"
              name="rollingOffer.backgroundColor"
              defaultValue={
                values?.rollingOffer?.backgroundColor || { colorOne: '#272727' }
              }
              onChange={handleChange}
              setValue={setFieldValue}
              onBlur={handleBlur}
              error={
                touched?.rollingOffer?.backgroundColor &&
                Boolean(errors?.rollingOffer?.backgroundColor)
              }
              helperText={
                touched?.rollingOffer?.backgroundColor
                  ? errors?.rollingOffer?.backgroundColor?.toString()
                  : ''
              }
            />
          </Grid>
          <Grid item xs={6}>
            <AcInput
              header="Background opacity"
              name="rollingOffer.backgroundOpacity"
              value={Number(values.rollingOffer?.backgroundOpacity ?? 80)}
              onChange={(e) => {
                setFieldValue(
                  'rollingOffer.backgroundOpacity',
                  Number(e.target.value)
                );
              }}
              onBlur={handleBlur}
              type="number"
              slotProps={{
                htmlInput: {
                  min: 0,
                  max: 100
                },
                input: {
                  startAdornment: (
                    <InputAdornment position="start">%</InputAdornment>
                  )
                }
              }}
              required
              error={
                touched.rollingOffer?.backgroundOpacity &&
                Boolean(errors?.rollingOffer?.backgroundOpacity)
              }
              helperText={
                touched.rollingOffer?.backgroundOpacity
                  ? errors?.rollingOffer?.backgroundOpacity?.toString()
                  : ''
              }
            />
          </Grid>
        </Grid>
        <Grid container columnSpacing={{ xs: 1 }} rowSpacing={0.5} mt={1}>
          <Grid item xs={6}>
            <AcSolidInput
              header="Arrows Default Color"
              name="rollingOffer.arrowColor"
              defaultValue={values?.rollingOffer?.arrowColor || '#ffffff'}
              setValue={setFieldValue}
              onBlur={handleBlur}
            />
          </Grid>
          {hasFeatureFlagArrowImage && (
            <Grid item xs={6}>
              <input
                style={{ display: 'none' }}
                type="file"
                onChange={(e) =>
                  uploadImage(
                    e,
                    addPictureInputRef.current as HTMLInputElement,
                    'backgroundImage',
                    setFieldValue,
                    setCurrentUploadingField,
                    currentUploadingField
                  )
                }
                ref={addPictureInputRef}
                accept="image/*"
              />
              <AcSelect
                header="Arrows Image"
                name="rollingOffer.arrowImage"
                value={values?.rollingOffer?.arrowImage || ''}
                uploadConfig={{
                  onUploadSuccess: async (uploadData: any) => {
                    await getImages.refetch();
                    setFieldValue(
                      'rollingOffer.arrowImage',
                      uploadData!.data.url
                    );
                  },
                  uploadType: EAssetType.GENERAL,
                  uploadMessage: getAssetUploadMessage(values.offerUiType)
                }}
                renderType={EAcSelectItemRenderType.IMAGE}
                items={[
                  defaultBackgroundImage,
                  ...getImages.data
                    .filter((p: any) => p.type === EAssetType.GENERAL)
                    .map((picture: any) => {
                      return {
                        content: picture.name,
                        key: picture.url,
                        value: picture.url,
                        url: picture.url,
                        name: picture.name
                      };
                    })
                ]}
                onChange={handleChange}
                onBlur={handleBlur}
                onClear={() => {
                  setFieldValue('rollingOffer.arrowImage', '');
                }}
                imgHeight={'32'}
                tooltip="Upload a 1:1 ratio image with an arrow pointing to the right."
              />
            </Grid>
          )}
          <Grid item xs={6}>
            <AcGradientInput
              header="Offer border"
              name="borderColor"
              defaultValue={values.borderColor}
              onChange={handleChange}
              setValue={setFieldValue}
              onBlur={handleBlur}
              imgWidth={'32px'}
              imgHeight={'32px'}
              roundedBorders={false}
            />
          </Grid>
          <Grid item xs={6}>
            <AcInput
              header="Offer border width"
              name="borderWidth"
              type="number"
              required
              value={Number(values.borderWidth)}
              onFocus={(e) => e.target.select()}
              onMouseUp={(e) => (e.target as HTMLInputElement).select()}
              onChange={(e) => {
                const newValue = Number(e.target.value);
                if (newValue >= 0 && newValue <= 8) {
                  setFieldValue('borderWidth', newValue);
                }
              }}
              onBlur={handleBlur}
              slotProps={{
                htmlInput: {
                  min: 0,
                  max: 8
                },
                input: {
                  endAdornment: (
                    <InputAdornment position="end">px</InputAdornment>
                  )
                }
              }}
              onKeyDown={(e) => {
                const currentValue = Number(values.borderWidth);
                if (
                  (e.key === 'ArrowUp' && currentValue >= 8) ||
                  (e.key === 'ArrowDown' && currentValue <= 0)
                ) {
                  e.preventDefault();
                }
              }}
              error={touched.borderWidth && Boolean(errors?.borderWidth)}
              helperText={
                touched.borderWidth ? errors?.borderWidth?.toString() : ''
              }
            />
          </Grid>
        </Grid>
      </AcCard>
      <Divider />
      <AcCard
        stackContainer={false}
        title="Sub Offer Design"
        padded={false}
        className="offersUIFormCard"
      >
        <Grid container columnSpacing={{ xs: 1 }}>
          <Grid item xs={6}>
            <input
              style={{ display: 'none' }}
              type="file"
              onChange={(e) =>
                uploadImage(
                  e,
                  addPictureInputRef.current as HTMLInputElement,
                  'rollingOffer.subRollingOffer.backgroundImage',
                  setFieldValue,
                  setCurrentUploadingField,
                  currentUploadingField
                )
              }
              ref={addPictureInputRef}
              accept="image/*"
            />
            <AcSelect
              header="Offers background image"
              name="rollingOffer.subRollingOffer.backgroundImage"
              value={
                values?.rollingOffer?.subRollingOffer?.backgroundImage || ''
              }
              uploadConfig={{
                onUploadSuccess: async (uploadData: any) => {
                  await getImages.refetch();
                  setFieldValue(
                    'rollingOffer.subRollingOffer.backgroundImage',
                    uploadData!.data.url
                  );
                },
                uploadType: EAssetType.BG_BUNDLE,
                uploadMessage: getAssetUploadMessage(values.offerUiType)
              }}
              renderType={EAcSelectItemRenderType.IMAGE}
              items={[
                defaultBackgroundImage,
                ...getImages.data
                  .filter((p: any) => p.type === EAssetType.BG_BUNDLE)
                  .map((picture: any) => {
                    return {
                      content: picture.name,
                      key: picture.url,
                      value: picture.url,
                      url: picture.url,
                      name: picture.name
                    };
                  })
              ]}
              onChange={handleChange}
              onBlur={handleBlur}
              onClear={() => {
                setFieldValue(
                  'rollingOffer.subRollingOffer.backgroundImage',
                  ''
                );
              }}
              error={
                touched?.rollingOffer?.subRollingOffer?.backgroundImage &&
                Boolean(errors?.rollingOffer?.subRollingOffer?.backgroundImage)
              }
              helperText={
                touched?.rollingOffer?.subRollingOffer?.backgroundImage || dup
                  ? errors?.rollingOffer?.subRollingOffer?.backgroundImage?.toString()
                  : ''
              }
              imgHeight={'32'}
            />
          </Grid>
          <Grid item xs={6}>
            <AcGradientInput
              required={true}
              header="Offers background color"
              name="rollingOffer.subRollingOffer.backgroundColor"
              defaultValue={
                values?.rollingOffer?.subRollingOffer?.backgroundColor || {
                  colorOne: '#FFFFFF'
                }
              }
              onChange={handleChange}
              setValue={setFieldValue}
              onBlur={handleBlur}
              error={
                touched?.rollingOffer?.subRollingOffer?.backgroundColor &&
                Boolean(errors?.rollingOffer?.subRollingOffer?.backgroundColor)
              }
              helperText={
                touched.rollingOffer?.subRollingOffer?.backgroundColor
                  ? errors.rollingOffer?.subRollingOffer?.backgroundColor?.toString()
                  : ''
              }
            />
          </Grid>
          <Grid item xs={6}>
            <AcInput
              required
              header="Collect Text"
              name="rollingOffer.subRollingOffer.collectText"
              value={values?.rollingOffer?.subRollingOffer?.collectText}
              placeholder="FREE"
              onChange={handleChange}
              onBlur={handleBlur}
              error={
                touched.rollingOffer?.subRollingOffer?.collectText &&
                Boolean(errors.rollingOffer?.subRollingOffer?.collectText)
              }
              helperText={
                touched.rollingOffer?.subRollingOffer?.collectText || dup
                  ? errors.rollingOffer?.subRollingOffer?.collectText?.toString()
                  : ''
              }
            />
          </Grid>
        </Grid>
      </AcCard>
    </>
  );
};

export default RollingOfferUiForm;
